import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type {
  FormSchemaVersion,
  BespokeTemplateResponse,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { FormTree, SectionLayoutState } from './formSchemaTypes';
import { formatSectionDropdownLabel } from './formatSectionDropdownLabel';
import {
  applyMatchingLayoutCopyFromSource,
  fetchMatchingLiveLayoutSourcesForSection,
  formatMatchingCopyAttribution,
  type MatchingLiveLayoutSource,
} from './matchingLayoutCopyShared';
import {
  bespokeApiErrorMessage,
  getSectionLayoutState,
  pinFromFormSchemaVersion,
} from './formSchemaUtils';
import { SectionLayoutStatusBadge } from './SectionLayoutStatusBadge';

const SKIP_VALUE = '__skip__';

export type FormBulkMatchingLayoutCopyDialogToast = {
  error: (title: string, description?: string) => void;
  success: (title: string, description?: string) => void;
};

export type SectionMatchPlan = {
  sectionName: string;
  matches: MatchingLiveLayoutSource[];
  lookupError: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formTree: FormTree | null;
  selectedVersion: FormSchemaVersion | null;
  selectedRogerDocFormType: RogerDocFormType | null;
  /** Primary live/paused template per section — same map as Form Tree layout column. */
  layoutTemplateBySection: Map<string, BespokeTemplateResponse>;
  existingTemplateBySection: Map<string, BespokeTemplateResponse>;
  toast: FormBulkMatchingLayoutCopyDialogToast;
  /** Patch local state with saved rows (avoids refetching the full list). */
  onTemplatesSaved: (saved: BespokeTemplateResponse[]) => void;
};

function sectionRadioGroupId(sectionName: string): string {
  return `bulk-match-${encodeURIComponent(sectionName).replace(/%/g, '_')}`;
}

function matchesFormNameFilter(
  match: MatchingLiveLayoutSource,
  filterText: string
): boolean {
  const q = filterText.trim().toLowerCase();
  if (!q) return true;
  return match.schemaVersion.name.toLowerCase().includes(q);
}

function filterMatchesByFormName(
  matches: MatchingLiveLayoutSource[],
  filterText: string
): MatchingLiveLayoutSource[] {
  return matches.filter(m => matchesFormNameFilter(m, filterText));
}

function isValidLiveLayoutSection(layoutState: SectionLayoutState): boolean {
  return layoutState.kind === 'valid' && layoutState.template.status === 'live';
}

function sectionLayoutStateForPlan(
  plan: SectionMatchPlan,
  formTree: FormTree,
  layoutTemplateBySection: Map<string, BespokeTemplateResponse>
): SectionLayoutState {
  const questions = formTree[plan.sectionName] ?? [];
  return getSectionLayoutState(
    layoutTemplateBySection.get(plan.sectionName),
    questions
  );
}

export function FormBulkMatchingLayoutCopyDialog({
  open,
  onOpenChange,
  formTree,
  selectedVersion,
  selectedRogerDocFormType,
  layoutTemplateBySection,
  existingTemplateBySection,
  toast,
  onTemplatesSaved,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [plans, setPlans] = useState<SectionMatchPlan[]>([]);
  const [selectionBySection, setSelectionBySection] = useState<
    Record<string, string>
  >({});
  const [formNameFilter, setFormNameFilter] = useState('');
  const [hideValidLiveSections, setHideValidLiveSections] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setLoadError(null);
      setPlans([]);
      setSelectionBySection({});
      setFormNameFilter('');
      setHideValidLiveSections(false);
      setApplying(false);
      return;
    }

    if (
      !selectedVersion ||
      !selectedRogerDocFormType ||
      !formTree ||
      Object.keys(formTree).length === 0
    ) {
      return;
    }

    let cancelled = false;
    void (async () => {
      setLoading(true);
      setLoadError(null);
      setPlans([]);
      setSelectionBySection({});

      const entries = Object.entries(formTree).sort(([a], [b]) =>
        formatSectionDropdownLabel(a).localeCompare(
          formatSectionDropdownLabel(b),
          undefined,
          { sensitivity: 'base' }
        )
      );

      try {
        const destinationPin = pinFromFormSchemaVersion(
          selectedRogerDocFormType,
          selectedVersion
        );
        if (!destinationPin) {
          setLoadError('The selected schema is missing a version identifier.');
          return;
        }

        const results = await Promise.all(
          entries.map(async ([sectionName, questions]) => {
            if (!questions?.length) {
              return {
                sectionName,
                matches: [] as MatchingLiveLayoutSource[],
                lookupError: null as string | null,
              };
            }
            try {
              const matches = await fetchMatchingLiveLayoutSourcesForSection({
                destinationSectionName: sectionName,
                destinationFormSchemaVersionPin: destinationPin,
                questions,
              });
              return { sectionName, matches, lookupError: null };
            } catch (err) {
              return {
                sectionName,
                matches: [] as MatchingLiveLayoutSource[],
                lookupError: bespokeApiErrorMessage(err),
              };
            }
          })
        );

        if (cancelled) return;

        setPlans(results);
        const initSel: Record<string, string> = {};
        for (const r of results) {
          initSel[r.sectionName] = SKIP_VALUE;
        }
        setSelectionBySection(initSel);
      } catch (err) {
        if (cancelled) return;
        setLoadError(bespokeApiErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formTree, open, selectedRogerDocFormType, selectedVersion]);

  useEffect(() => {
    const q = formNameFilter.trim();
    if (!q || plans.length === 0) return;

    setSelectionBySection(prev => {
      let changed = false;
      const next = { ...prev };
      for (const plan of plans) {
        const sel = next[plan.sectionName];
        if (!sel || sel === SKIP_VALUE) continue;
        const stillVisible = plan.matches.some(
          m => m.template.id === sel && matchesFormNameFilter(m, q)
        );
        if (!stillVisible) {
          next[plan.sectionName] = SKIP_VALUE;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [formNameFilter, plans]);

  const visiblePlans = useMemo(() => {
    if (!formTree || !hideValidLiveSections) return plans;
    return plans.filter(plan => {
      const layoutState = sectionLayoutStateForPlan(
        plan,
        formTree,
        layoutTemplateBySection
      );
      return !isValidLiveLayoutSection(layoutState);
    });
  }, [formTree, hideValidLiveSections, layoutTemplateBySection, plans]);

  useEffect(() => {
    if (!hideValidLiveSections || !formTree || plans.length === 0) return;

    setSelectionBySection(prev => {
      let changed = false;
      const next = { ...prev };
      for (const plan of plans) {
        const layoutState = sectionLayoutStateForPlan(
          plan,
          formTree,
          layoutTemplateBySection
        );
        if (!isValidLiveLayoutSection(layoutState)) continue;
        if (next[plan.sectionName] !== SKIP_VALUE) {
          next[plan.sectionName] = SKIP_VALUE;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [formTree, hideValidLiveSections, layoutTemplateBySection, plans]);

  const copyCount = useMemo(() => {
    let n = 0;
    for (const p of visiblePlans) {
      const sel = selectionBySection[p.sectionName];
      if (!sel || sel === SKIP_VALUE) continue;
      const visible = filterMatchesByFormName(p.matches, formNameFilter);
      if (visible.some(m => m.template.id === sel)) n++;
    }
    return n;
  }, [formNameFilter, selectionBySection, visiblePlans]);

  const handleApply = useCallback(async () => {
    if (
      !selectedVersion ||
      !selectedRogerDocFormType ||
      !formTree ||
      copyCount === 0
    )
      return;

    setApplying(true);
    const failures: string[] = [];
    const saved: BespokeTemplateResponse[] = [];
    let ok = 0;

    for (const plan of visiblePlans) {
      const sel = selectionBySection[plan.sectionName];
      if (!sel || sel === SKIP_VALUE) continue;
      const visible = filterMatchesByFormName(plan.matches, formNameFilter);
      const match = visible.find(m => m.template.id === sel);
      const questions = formTree[plan.sectionName];
      if (!match || !questions?.length) continue;

      try {
        const savedTemplate = await applyMatchingLayoutCopyFromSource({
          match,
          destinationSectionName: plan.sectionName,
          questions,
          selectedRogerDocFormType,
          selectedVersion,
          existingTemplateBySection,
        });
        saved.push(savedTemplate);
        ok++;
      } catch (err) {
        const label = formatSectionDropdownLabel(plan.sectionName);
        failures.push(`${label}: ${bespokeApiErrorMessage(err)}`);
      }
    }

    setApplying(false);

    if (saved.length > 0) {
      onTemplatesSaved(saved);
    }

    if (failures.length === 0 && ok > 0) {
      toast.success(
        'Layouts copied',
        ok === 1
          ? 'Copied 1 section layout from matching live templates.'
          : `Copied ${ok} section layouts from matching live templates.`
      );
      onOpenChange(false);
    } else if (ok > 0 && failures.length > 0) {
      toast.error(
        'Partial copy',
        `${ok} succeeded. Failed: ${failures.slice(0, 3).join(' · ')}${
          failures.length > 3 ? ` (+${failures.length - 3} more)` : ''
        }`
      );
    } else if (failures.length > 0) {
      toast.error(
        'Copy failed',
        failures.slice(0, 4).join(' · ') +
          (failures.length > 4 ? ` (+${failures.length - 4} more)` : '')
      );
    }
  }, [
    copyCount,
    existingTemplateBySection,
    onTemplatesSaved,
    formNameFilter,
    formTree,
    onOpenChange,
    selectedRogerDocFormType,
    selectedVersion,
    selectionBySection,
    toast,
    visiblePlans,
  ]);

  const disabled =
    !selectedVersion ||
    !selectedRogerDocFormType ||
    !formTree ||
    applying ||
    loading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[min(92vh,56rem)] flex flex-col'>
        <DialogHeader>
          <DialogTitle>Copy live layouts from matching sections</DialogTitle>
          <DialogDescription>
            For each section in this form, optionally pick a live bespoke layout
            whose QIDs match that section.
          </DialogDescription>
        </DialogHeader>

        {!selectedVersion || !selectedRogerDocFormType ? (
          <p className='text-sm text-muted-foreground'>
            Select a form type and schema version first.
          </p>
        ) : !formTree || Object.keys(formTree).length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            Load a schema version with sections before copying layouts.
          </p>
        ) : loading ? (
          <div
            className='flex min-h-[12rem] max-h-[min(48vh,24rem)] flex-col items-center justify-center gap-2 rounded-md border border-border bg-muted/30 text-sm text-muted-foreground'
            role='status'
            aria-busy='true'
          >
            <Loader2 className='h-5 w-5 animate-spin' aria-hidden />
            <span>Looking up matching layouts for each section…</span>
          </div>
        ) : loadError ? (
          <div className='rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
            {loadError}
          </div>
        ) : (
          <div className='min-h-0 flex flex-1 flex-col gap-2'>
            <div className='shrink-0 space-y-2'>
              <div className='space-y-1'>
                <Label
                  htmlFor='bulk-match-form-name-filter'
                  className='text-xs font-medium text-muted-foreground'
                >
                  Filter matches by form name
                </Label>
                <Input
                  id='bulk-match-form-name-filter'
                  value={formNameFilter}
                  onChange={e => setFormNameFilter(e.target.value)}
                  placeholder='e.g. SOC Initial'
                  disabled={disabled}
                  className='h-9'
                />
              </div>
              <div className='flex items-center gap-2'>
                <Checkbox
                  id='bulk-match-hide-valid-sections'
                  checked={hideValidLiveSections}
                  onCheckedChange={checked =>
                    setHideValidLiveSections(checked === true)
                  }
                  disabled={disabled}
                />
                <Label
                  htmlFor='bulk-match-hide-valid-sections'
                  className='cursor-pointer text-xs font-normal text-muted-foreground'
                >
                  Hide sections with a valid live layout
                </Label>
              </div>
            </div>
            <div className='flex flex-wrap gap-2 shrink-0'>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-8 px-2'
                disabled={disabled || visiblePlans.length === 0}
                onClick={() => {
                  const next: Record<string, string> = {
                    ...selectionBySection,
                  };
                  for (const p of visiblePlans) {
                    const visible = filterMatchesByFormName(
                      p.matches,
                      formNameFilter
                    );
                    next[p.sectionName] = visible[0]?.template.id ?? SKIP_VALUE;
                  }
                  setSelectionBySection(next);
                }}
              >
                Select first match where available
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-8 px-2'
                disabled={disabled || visiblePlans.length === 0}
                onClick={() => {
                  const next: Record<string, string> = {
                    ...selectionBySection,
                  };
                  for (const p of visiblePlans) {
                    next[p.sectionName] = SKIP_VALUE;
                  }
                  setSelectionBySection(next);
                }}
              >
                Clear all sections
              </Button>
            </div>

            <div className='min-h-[12rem] max-h-[min(48vh,28rem)] overflow-y-auto overflow-x-auto rounded-md border border-border'>
              {visiblePlans.length === 0 ? (
                <p className='px-3 py-6 text-center text-sm text-muted-foreground'>
                  {hideValidLiveSections
                    ? 'All sections have a valid live layout, or none remain after filtering.'
                    : 'No sections to show.'}
                </p>
              ) : (
                visiblePlans.map(plan => {
                  const rgId = sectionRadioGroupId(plan.sectionName);
                  const sectionQuestions = formTree?.[plan.sectionName] ?? [];
                  const layoutState = getSectionLayoutState(
                    layoutTemplateBySection.get(plan.sectionName),
                    sectionQuestions
                  );
                  const visibleMatches = filterMatchesByFormName(
                    plan.matches,
                    formNameFilter
                  );
                  const filterActive = formNameFilter.trim().length > 0;
                  const value =
                    selectionBySection[plan.sectionName] ?? SKIP_VALUE;
                  const selectedStillVisible =
                    value === SKIP_VALUE ||
                    visibleMatches.some(m => m.template.id === value);
                  const radioValue = selectedStillVisible ? value : SKIP_VALUE;

                  return (
                    <div
                      key={plan.sectionName}
                      className='border-b border-border last:border-b-0'
                    >
                      <div className='flex flex-wrap items-center justify-between gap-2 bg-muted/30 px-3 py-2'>
                        <span className='flex min-w-0 items-center gap-2 text-sm font-medium text-foreground'>
                          <SectionLayoutStatusBadge state={layoutState} />
                          <span className='truncate'>
                            {formatSectionDropdownLabel(plan.sectionName)}
                          </span>
                        </span>
                        <Badge variant='outline' className='tabular-nums'>
                          {filterActive && plan.matches.length > 0
                            ? `${visibleMatches.length} / ${plan.matches.length} match${
                                plan.matches.length === 1 ? '' : 'es'
                              }`
                            : `${plan.matches.length} match${
                                plan.matches.length === 1 ? '' : 'es'
                              }`}
                        </Badge>
                      </div>
                      <div className='bg-muted/15 px-3 py-3'>
                        {plan.lookupError ? (
                          <p className='text-xs text-destructive'>
                            {plan.lookupError}
                          </p>
                        ) : plan.matches.length === 0 ? (
                          <p className='text-xs text-muted-foreground'>
                            No other live layouts share this section&apos;s QID
                            set.
                          </p>
                        ) : visibleMatches.length === 0 ? (
                          <p className='text-xs text-muted-foreground'>
                            No matches for this form name filter.
                          </p>
                        ) : (
                          <RadioGroup
                            value={radioValue}
                            onValueChange={next =>
                              setSelectionBySection(prev => ({
                                ...prev,
                                [plan.sectionName]: next,
                              }))
                            }
                            className='gap-2'
                          >
                            <div className='flex gap-3 rounded-md border border-border bg-background p-2.5'>
                              <RadioGroupItem
                                value={SKIP_VALUE}
                                id={`${rgId}-skip`}
                                className='mt-0.5 shrink-0'
                              />
                              <Label
                                htmlFor={`${rgId}-skip`}
                                className='cursor-pointer text-xs font-normal leading-snug text-muted-foreground'
                              >
                                Don&apos;t copy for this section
                              </Label>
                            </div>
                            {visibleMatches.map(match => (
                              <div
                                key={match.template.id}
                                className='flex gap-3 rounded-md border border-border bg-background p-2.5'
                              >
                                <RadioGroupItem
                                  value={match.template.id}
                                  id={`${rgId}-${match.template.id}`}
                                  className='mt-0.5 shrink-0'
                                />
                                <Label
                                  htmlFor={`${rgId}-${match.template.id}`}
                                  className='cursor-pointer text-xs font-normal leading-snug'
                                >
                                  <span className='block text-foreground'>
                                    {formatMatchingCopyAttribution(match)}
                                  </span>
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        <DialogFooter className='shrink-0'>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={applying}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={() => void handleApply()}
            disabled={
              applying ||
              loading ||
              copyCount === 0 ||
              !selectedVersion ||
              !formTree
            }
          >
            {applying && (
              <Loader2 className='h-4 w-4 mr-2 animate-spin' aria-hidden />
            )}
            Copy {copyCount} layout{copyCount === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
